import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, FileSpreadsheet, Trash2, Lock, Unlock, Maximize2, Copy } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AdminTitle, Spinner } from "@/admin/AdminLayout";
import { Badge, Empty, copyText, download, useList } from "@/admin/hr/common";

export const AF_STATUSES = ["Yeni Başvuru", "İnceleniyor", "Uygun", "Uygun Değil"];
export const AF_PAYMENTS = ["Ödeme Bekliyor", "Ödendi"];
const PRESETS = [["all", "Tüm başvurular"], ["suitable", "Uygun bulunanlar"], ["unsuitable", "Uygun bulunmayanlar"], ["pending_review", "İncelenmeyi bekleyenler"], ["payment_pending", "Ödeme bekleyenler"], ["paid", "Ödemesi yapılanlar"]];
const HEAD = ["Kayıt No", "Tarih", "Ad Soyad", "Telefon", "IBAN", "Tutar", "İnceleme", "Ödeme"];
const EMPTY_F = { q: "", status: "", payment_status: "", date_from: "", date_to: "", preset: "all" };
const tl = (n) => `${Number(n || 0).toLocaleString("tr-TR")} TL`;
const PAY_CLS = { "Ödendi": "bg-emerald-100 text-emerald-800", "Ödeme Bekliyor": "bg-amber-100 text-amber-800" };

function PayBadge({ value }) {
  return <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${PAY_CLS[value]}`}>{value}</span>;
}

function SettingsBar({ s, reload }) {
  const [amount, setAmount] = useState(s.default_amount);
  const save = async (body) => {
    try {
      await api.put("/admin/aydinfest/settings", { open: s.open, default_amount: Number(amount), ...body });
      toast.success("Kaydedildi");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = () => {
    const msg = s.open ? "Başvuru alımı kapatılsın mı? Formda 'Bilgi toplama süreci sona ermiştir.' mesajı görünecek; mevcut kayıtlar silinmez." : "Başvuru alımı yeniden açılsın mı?";
    if (window.confirm(msg)) save({ open: !s.open });
  };
  return (
    <div className="mb-4 flex flex-wrap items-end gap-4 rounded-lg border border-zinc-200 bg-white p-4" data-testid="af-settings">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Başvuru alımı</p>
        <p className={`text-sm font-bold ${s.open ? "text-emerald-700" : "text-red-700"}`} data-testid="af-open-state">{s.open ? "Açık" : "Kapalı"}</p>
      </div>
      <button onClick={toggle} className={s.open ? "btn-outline !py-2" : "btn-red !py-2"} data-testid="af-toggle-open">{s.open ? <><Lock className="h-4 w-4" /> Başvuruları Kapat</> : <><Unlock className="h-4 w-4" /> Başvuruları Aç</>}</button>
      <label className="text-sm"><span className="field-label">Varsayılan destek tutarı (TL)</span><input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="field-input w-32" data-testid="af-default-amount" /></label>
      <button onClick={() => save({})} className="btn-outline !py-2" data-testid="af-save-amount">Tutarı Kaydet</button>
      <p className="w-full text-xs text-zinc-500">Varsayılan tutar yeni gelen başvurulara uygulanır; mevcut kayıtların tutarı kayıt detayından değiştirilir. Başvuru bağlantısı: <b>/aydinfest-basvuru</b></p>
    </div>
  );
}

function Stat({ label, value, sub, testid }) {
  return <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={testid}><p className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</p><p className="mt-1 text-2xl font-extrabold">{value}</p>{sub && <p className="text-xs text-zinc-500">{sub}</p>}</div>;
}

function Summary({ s }) {
  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <Stat label="Toplam" value={s.total} testid="af-stat-total" />
      {AF_STATUSES.map((k) => <Stat key={k} label={k} value={s.by_status[k]} testid={`af-stat-${k}`} />)}
      <Stat label="Ödenen" value={tl(s.paid_total)} sub={`${s.paid_count} kişi · Bekleyen: ${tl(s.due_total)} (${s.due_count} kişi)`} testid="af-stat-paid" />
    </div>
  );
}

function Filters({ f, setF }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="mb-4 grid gap-3 rounded-lg border border-zinc-200 bg-white p-4 sm:grid-cols-3 lg:grid-cols-6">
      <div className="relative sm:col-span-3 lg:col-span-2"><Search className="absolute left-3 top-3.5 h-4 w-4 text-zinc-400" /><input value={f.q} onChange={set("q")} placeholder="Ad soyad, telefon veya kayıt no" className="field-input !pl-9" data-testid="af-filter-q" /></div>
      <select value={f.preset} onChange={set("preset")} className="field-input" data-testid="af-filter-preset">{PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <select value={f.status} onChange={set("status")} className="field-input" data-testid="af-filter-status"><option value="">Tüm inceleme durumları</option>{AF_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      <select value={f.payment_status} onChange={set("payment_status")} className="field-input" data-testid="af-filter-payment"><option value="">Tüm ödeme durumları</option>{AF_PAYMENTS.map((s) => <option key={s}>{s}</option>)}</select>
      <div className="grid grid-cols-2 gap-2">
        <input type="date" value={f.date_from} onChange={set("date_from")} className="field-input" aria-label="Başlangıç tarihi" data-testid="af-filter-from" />
        <input type="date" value={f.date_to} onChange={set("date_to")} className="field-input" aria-label="Bitiş tarihi" data-testid="af-filter-to" />
      </div>
    </div>
  );
}

function AfTable({ rows, onOpen }) {
  if (!rows) return <Spinner />;
  if (rows.length === 0) return <Empty testid="af-empty" />;
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table className="w-full text-sm" data-testid="af-table">
        <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500"><tr>{HEAD.map((h) => <th key={h} className="whitespace-nowrap px-3 py-3">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r.id)} className="cursor-pointer hover:bg-zinc-50" data-testid={`af-row-${r.id}`}>
              <td className="whitespace-nowrap px-3 py-3 font-semibold text-[color:var(--red)]">{r.ref_no}</td>
              <td className="whitespace-nowrap px-3 py-3 text-zinc-500">{fmtDate(r.created_at)}</td>
              <td className="px-3 py-3 font-semibold">{r.name}</td>
              <td className="whitespace-nowrap px-3 py-3">{r.phone}</td>
              <td className="whitespace-nowrap px-3 py-3 font-mono text-xs">{r.iban}{r.iban_shared_with && <span className="ml-1 rounded bg-orange-100 px-1 font-sans font-semibold text-orange-800" title={`Aynı IBAN: ${r.iban_shared_with}`}>Ortak IBAN</span>}</td>
              <td className="whitespace-nowrap px-3 py-3">{tl(r.amount)}</td>
              <td className="px-3 py-3"><Badge value={r.status} /></td>
              <td className="px-3 py-3"><PayBadge value={r.payment_status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function usePhoto(id) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/admin/aydinfest/applications/${id}/photo`, { responseType: "blob" })
      .then((r) => { u = URL.createObjectURL(r.data); setUrl(u); })
      .catch(() => toast.error("Fotoğraf yüklenemedi"));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [id]);
  return url;
}

function PhotoView({ id }) {
  const url = usePhoto(id);
  const [big, setBig] = useState(false);
  if (!url) return <div className="flex h-48 items-center justify-center rounded-lg bg-zinc-100 text-sm text-zinc-500">Fotoğraf yükleniyor...</div>;
  return (
    <>
      <button type="button" onClick={() => setBig(true)} className="group relative block" data-testid="af-photo-thumb">
        <img src={url} alt="Konser alanı fotoğrafı" className="max-h-72 rounded-lg border border-zinc-200" />
        <span className="absolute right-2 top-2 rounded bg-black/70 p-1.5 text-white"><Maximize2 className="h-4 w-4" /></span>
      </button>
      <Dialog open={big} onOpenChange={setBig}>
        <DialogContent className="max-w-[95vw] border-0 bg-black p-2" data-testid="af-photo-lightbox">
          <DialogHeader className="sr-only"><DialogTitle>Fotoğraf (büyük)</DialogTitle></DialogHeader>
          <img src={url} alt="Konser alanı fotoğrafı (büyük)" className="max-h-[88vh] w-full object-contain" />
        </DialogContent>
      </Dialog>
    </>
  );
}

function PaymentPanel({ c, patch }) {
  const [amount, setAmount] = useState(c.amount);
  return (
    <div className="grid gap-3 rounded-lg border border-zinc-200 p-4 sm:grid-cols-3" data-testid="af-payment-panel">
      <label className="text-sm"><span className="field-label">Ödeme durumu</span>
        <select value={c.payment_status} onChange={(e) => patch({ payment_status: e.target.value })} className="field-input" data-testid="af-payment-select">{AF_PAYMENTS.map((s) => <option key={s}>{s}</option>)}</select>
      </label>
      <label className="text-sm"><span className="field-label">Ödeme tarihi</span><input type="date" value={c.payment_date || ""} disabled={c.payment_status !== "Ödendi"} onChange={(e) => patch({ payment_date: e.target.value })} className="field-input" data-testid="af-payment-date" /></label>
      <label className="text-sm"><span className="field-label">Destek tutarı (TL)</span><input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} onBlur={() => Number(amount) !== c.amount && patch({ amount: Number(amount) })} className="field-input" data-testid="af-amount" /></label>
      <p className="text-xs text-zinc-500 sm:col-span-3">Ödeme yalnızca inceleme durumu “Uygun” olan başvurular için işaretlenebilir. Ödendi seçildiğinde tarih boşsa bugünün tarihi yazılır.</p>
    </div>
  );
}

function NoteBox({ c, patch }) {
  const [note, setNote] = useState(c.admin_note || "");
  return (
    <label className="block text-sm"><span className="field-label">Yönetici notu</span>
      <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="field-input" maxLength={2000} data-testid="af-note" />
      <button type="button" disabled={note === (c.admin_note || "")} onClick={() => patch({ admin_note: note })} className="btn-outline mt-2 !py-2 disabled:opacity-50" data-testid="af-note-save">Notu Kaydet</button>
    </label>
  );
}

function History({ log }) {
  if (!log?.length) return null;
  return (
    <div className="rounded-lg border border-zinc-200 p-4 text-xs" data-testid="af-history">
      <p className="mb-2 font-bold uppercase tracking-wider text-zinc-500">İşlem geçmişi</p>
      <ul className="space-y-1">{[...log].reverse().map((l) => <li key={l.at}><b>{fmtDate(l.at)}</b> · {l.by}: {l.changes.join("; ")}</li>)}</ul>
    </div>
  );
}

function Detail({ id, onChanged, onClose }) {
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get(`/admin/aydinfest/applications/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  if (!c) return <DialogContent><Spinner /></DialogContent>;
  const patch = async (body) => {
    try {
      setC((await api.patch(`/admin/aydinfest/applications/${id}`, body)).data);
      toast.success("Kaydedildi");
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm(`${c.ref_no} kaydı ve fotoğrafı kalıcı olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/admin/aydinfest/applications/${id}`);
      toast.success("Kayıt silindi");
      onClose();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="af-detail">
      <DialogHeader><DialogTitle>{c.name} <span className="ml-2 text-sm text-[color:var(--red)]">{c.ref_no}</span></DialogTitle></DialogHeader>
      <p className="text-xs text-zinc-500">Başvuru: {fmtDate(c.created_at)} · Açık rıza (iletişim): {c.explicit_consent ? "Verildi" : "Verilmedi"}</p>
      {c.iban_shared_with && <p className="rounded-lg bg-orange-50 p-3 text-sm font-semibold text-orange-800" data-testid="af-iban-shared">Dikkat: Bu IBAN daha önce {c.iban_shared_with} numaralı başvuruda da kullanılmış. IBAN'ın başvuru sahibine ait olduğunu kontrol edin.</p>}
      <dl className="grid gap-3 rounded-lg border border-zinc-200 p-4 text-sm sm:grid-cols-2">
        <div><dt className="text-xs font-bold uppercase tracking-wider text-zinc-500">Telefon</dt><dd><a href={`tel:${c.phone}`} className="font-semibold">{c.phone}</a></dd></div>
        <div><dt className="text-xs font-bold uppercase tracking-wider text-zinc-500">IBAN</dt><dd className="flex items-center gap-2 font-mono text-xs" data-testid="af-detail-iban">{c.iban}<button type="button" onClick={() => copyText(c.iban)} aria-label="IBAN kopyala" data-testid="af-copy-iban"><Copy className="h-4 w-4" /></button></dd></div>
      </dl>
      <PhotoView id={id} />
      <label className="text-sm"><span className="field-label">İnceleme durumu</span>
        <select value={c.status} onChange={(e) => patch({ status: e.target.value })} className="field-input" data-testid="af-status-select">{AF_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
      </label>
      <PaymentPanel key={`${c.amount}`} c={c} patch={patch} />
      <NoteBox c={c} patch={patch} />
      <History log={c.log} />
      <button onClick={remove} className="inline-flex items-center gap-2 self-start text-sm font-semibold text-red-700 hover:underline" data-testid="af-delete-btn"><Trash2 className="h-4 w-4" /> Kaydı kalıcı olarak sil</button>
    </DialogContent>
  );
}

function BulkDelete({ f, count, onDone }) {
  const run = async () => {
    const typed = window.prompt(`Filtreye uyan ${count} kayıt ve fotoğrafları kalıcı olarak silinecek. Onaylamak için SİL yazın.`);
    if (typed === null) return;
    try {
      const r = await api.post("/admin/aydinfest/applications/delete-filtered", { confirm: typed.trim().toLocaleUpperCase("tr-TR") }, { params: f });
      toast.success(`${r.data.deleted} kayıt silindi`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return <button onClick={run} disabled={!count} className="btn-outline !py-2 text-red-700 disabled:opacity-50" data-testid="af-bulk-delete"><Trash2 className="h-4 w-4" /> Filtrelenenleri Sil</button>;
}

export default function AydinfestAdmin() {
  const [f, setF] = useState(EMPTY_F);
  const [rows, load] = useList("/admin/aydinfest/applications", f);
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(null);
  const loadSummary = () => api.get("/admin/aydinfest/summary").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    loadSummary();
  }, []);
  const changed = () => { load(); loadSummary(); };
  const preset = PRESETS.find(([k]) => k === f.preset)[1];
  return (
    <div>
      <AdminTitle title="Aydınfest 2026 Başvuruları" desc={rows ? `${rows.length} kayıt listeleniyor · ${preset}` : ""}>
        <button onClick={() => download("/admin/aydinfest/export.xlsx", `aydinfest-${f.preset}.xlsx`, { params: f })} className="btn-red !py-2" data-testid="af-export-xlsx"><FileSpreadsheet className="h-4 w-4" /> EXCEL’E AKTAR (.XLSX)</button>
        <BulkDelete f={f} count={rows?.length || 0} onDone={changed} />
      </AdminTitle>
      {s && <SettingsBar key={`${s.open}-${s.default_amount}`} s={s} reload={loadSummary} />}
      {s && <Summary s={s} />}
      <Filters f={f} setF={setF} />
      <p className="mb-2 text-xs text-zinc-500">Excel’e aktar, yalnızca yukarıdaki filtrelere uyan kayıtları indirir. Rapor türü listesinden uygun / uygun değil / incelenmeyi bekleyen / ödeme bekleyen / ödemesi yapılan raporlarını seçebilirsiniz.</p>
      <AfTable rows={rows} onOpen={setOpen} />
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>{open && <Detail key={open} id={open} onChanged={changed} onClose={() => setOpen(null)} />}</Dialog>
    </div>
  );
}
